import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { NavLink } from "@/components/NavLink";
import {
  LayoutDashboard,
  Package,
  Tag,
  ShoppingCart,
  Truck,
  CreditCard,
  Users as UsersIcon,
  FileText,
  Image as ImageIcon,
  PanelBottom,
  FileEdit,
  MessageSquare,
  Settings as SettingsIcon,
  Store,
  LogOut,
  Menu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from "@/components/ui/sheet";
import { MessageBell, NotificationBell } from "./AdminNotifications";

const navItems = [
  { to: "/admin", icon: LayoutDashboard, label: "Overview", end: true },
  { to: "/admin/products", icon: Package, label: "Products" },
  { to: "/admin/categories", icon: Tag, label: "Categories" },
  { to: "/admin/orders", icon: ShoppingCart, label: "Orders" },
  { to: "/admin/shipping", icon: Truck, label: "Shipping" },
  { to: "/admin/payment", icon: CreditCard, label: "Payment" },
  { to: "/admin/users", icon: UsersIcon, label: "Users" },
  { to: "/admin/invoice", icon: FileText, label: "Invoice" },
  { to: "/admin/banners", icon: ImageIcon, label: "Banners" },
  { to: "/admin/messages", icon: MessageSquare, label: "Messages" },
  { to: "/admin/pages", icon: FileEdit, label: "Pages" },
  { to: "/admin/footer", icon: PanelBottom, label: "Footer" },
  { to: "/admin/settings", icon: SettingsIcon, label: "Settings" },
];

const SidebarContent = ({
  onNavigate,
  signOut,
  unread,
}: {
  onNavigate?: () => void;
  signOut: () => void;
  unread: number;
}) => (
  <div className="flex h-full flex-col">
    <div className="p-6 border-b border-sidebar-border">
      <h1 className="font-display text-2xl font-bold text-sidebar-primary-foreground">Rupshoili</h1>
      <p className="text-xs text-sidebar-foreground/60 mt-0.5">Admin panel</p>
    </div>
    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
      {navItems.map((item) => {
        const isMessages = item.to === "/admin/messages";
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onNavigate}
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-smooth hover:bg-sidebar-accent"
            activeClassName="bg-sidebar-primary text-sidebar-primary-foreground hover:bg-sidebar-primary"
          >
            <item.icon className="h-4 w-4" />
            <span className="flex-1">{item.label}</span>
            {isMessages && unread > 0 && (
              <span className="ml-auto min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full bg-destructive text-destructive-foreground text-[11px] font-bold">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </NavLink>
        );
      })}
    </nav>
    <div className="p-3 border-t border-sidebar-border space-y-1">
      <NavLink
        to="/"
        onClick={onNavigate}
        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-sidebar-accent"
      >
        <Store className="h-4 w-4" /> View store
      </NavLink>
      <button
        onClick={() => {
          onNavigate?.();
          signOut();
        }}
        className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-sidebar-accent"
      >
        <LogOut className="h-4 w-4" /> Sign out
      </button>
    </div>
  </div>
);

export const AdminLayout = () => {
  const { user, isAdmin, loading, signOut } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  // Close drawer on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Track unread message count for admin
  useEffect(() => {
    if (!isAdmin) return;
    const load = async () => {
      const { count } = await supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .eq("read_by_admin", false);
      setUnread(count ?? 0);
    };
    load();
    const ch = supabase
      .channel("admin-msg-count")
      .on("postgres_changes", { event: "*", schema: "public", table: "messages" }, load)
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [isAdmin]);

  if (loading) return null;
  if (!user) return <Navigate to={`/auth?redirect=${location.pathname}`} replace />;
  if (!isAdmin) return <Navigate to="/" replace />;

  const currentLabel =
    navItems.find((item) => (item.end ? location.pathname === item.to : location.pathname.startsWith(item.to)))?.label ??
    "Admin";

  return (
    <div className="flex min-h-screen bg-muted/40">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-sidebar text-sidebar-foreground flex-col">
        <SidebarContent signOut={signOut} unread={unread} />
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top header (desktop + mobile) */}
        <header className="sticky top-0 z-40 flex items-center gap-3 h-14 px-4 lg:px-6 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden h-10 w-10 -ml-2" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-72 p-0 bg-sidebar text-sidebar-foreground border-sidebar-border"
            >
              <SheetHeader className="sr-only">
                <SheetTitle>Admin navigation</SheetTitle>
              </SheetHeader>
              <SidebarContent onNavigate={() => setOpen(false)} signOut={signOut} unread={unread} />
            </SheetContent>
          </Sheet>
          <div className="flex flex-col leading-tight min-w-0">
            <span className="font-display text-base font-semibold truncate lg:hidden">Rupshoili</span>
            <span className="text-sm font-medium truncate hidden lg:block">{currentLabel}</span>
            <span className="text-xs text-muted-foreground truncate lg:hidden">{currentLabel}</span>
          </div>
          <div className="ml-auto flex items-center gap-1">
            <MessageBell />
            <NotificationBell />
          </div>
        </header>

        <main className="flex-1 min-w-0 overflow-x-auto">
          <div className="p-4 sm:p-6 lg:p-10 max-w-7xl w-full mx-auto lg:mx-0">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};
